import { Buyer, DocumentsSummary } from '@dill/dill-shared/dist/src';
import { useEffect, useState } from 'react';
import {
  getUserCustomers,
  recursiveGetUserCustomers,
} from '../../../../../redux/services/suppliersService';
import { getSupplierCollectionSequences } from '../../../../../redux/services/collectionSequenceService';
import { getSupplierCollectionTriggers } from '../../../../../redux/services/collectionTriggerService';
import { getSupplierPromiseToPays } from '../../../../../redux/services/promiseToPayService';
import { useCollectionBuyerEngagements } from '../../CollectionTriggersAndSequences/useCollectionBuyerEngagements';
import { useCollectionSequenceBuyerProgress } from '../../CollectionTriggersAndSequences/useCollectionSequenceBuyerProgress';
import { resolveBuyerCollectionEngagement } from '../../CollectionTriggersAndSequences/resolveBuyerCollectionEngagement';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { getSupplierBranches } from '../../../../../redux/services/supplierAdditionalDetailsService';

import { ArAgingRow } from './types';

const toSafeISOString = (value: unknown): string | null => {
  if (!value) return null;
  const date = new Date(value as any);
  return isNaN(date.getTime()) ? null : date.toISOString();
};

export const useArAgingData = () => {
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<ArAgingRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { customers, supplierUsers } = useAppSelector((state) => state.supplier);
  const { supplierPromiseToPays } = useAppSelector((state) => state.promiseToPays);
  const { user, firebaseUserId } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);
  const { sequences: collectionSequences } = useAppSelector((state) => state.collectionSequences);
  const { triggers: collectionTriggers } = useAppSelector((state) => state.collectionTriggers);
  const { engagementsByBuyerId } = useCollectionBuyerEngagements(currentSupplier?.id || '');
  const { progressByBuyerId } = useCollectionSequenceBuyerProgress(currentSupplier?.id || '');
  const [customerCount, setCustomerCount] = useState(0);
  const mainRoles = user?.roles.filter((role) => role !== 'DILL_ADMIN');
  const [previousLastDocumentId, setPreviousLastDocumentId] = useState('');

  useEffect(() => {
    if (!currentSupplier?.id) return;
    dispatch(getSupplierCollectionSequences({ supplierId: currentSupplier.id }));
    dispatch(getSupplierCollectionTriggers({ supplierId: currentSupplier.id }));
    dispatch(getSupplierPromiseToPays({ supplierId: currentSupplier.id }));
  }, [currentSupplier?.id]);

  const recursiveFetchCustomers = async (
    count: number,
    lastDockId: string,
    firstCustomers: {
      buyer: Buyer;
      usersEmailsDetails: {
        email: string;
        userId: string;
        isInvitedUser: boolean;
        isStatementsEmailOptOut: boolean;
        isPaymentRemindersEmailOptOut: boolean;
      }[];
      documentsSummary: DocumentsSummary;
    }[]
  ) => {
    if (firstCustomers.length > count) {
      setIsLoading(false);
      return;
    }
    setCustomerCount(count);
    const pageSize = 100;
    let currentPreviousLastDocumentId = lastDockId;
    for (let i = firstCustomers.length; i < count; i += pageSize) {
      const results = await dispatch(
        recursiveGetUserCustomers({
          supplierId: currentSupplier?.id || '',
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      if (results.type === 'supplier/recursiveGetUserCustomers/fulfilled') {
        if (results.payload) {
          const payload = results.payload as any;
          const fetchedCustomers: {
            buyer: Buyer;
            usersEmailsDetails: {
              email: string;
              isInvitedUser: boolean;
              isStatementsEmailOptOut: boolean;
              isPaymentRemindersEmailOptOut: boolean;
            }[];
            documentsSummary: DocumentsSummary;
          }[] = payload.data as any;
          const lastDocumentId = payload.lastDocumentId;
          if (lastDocumentId !== currentPreviousLastDocumentId) {
            setPreviousLastDocumentId(lastDocumentId);
            currentPreviousLastDocumentId = lastDocumentId;
          }
          if (!lastDockId || fetchedCustomers.length < pageSize) {
            console.log('No more pages to fetch');
            break;
          }
        }
      } else {
        console.log('Fetch failed or interrupted');
        break;
      }
    }
    setIsLoading(false);
  };

  const handleLoadMore = async (loadOptions?: { isFreshFetch?: boolean }) => {
    const pageSize = 100;
    let currentPreviousLastDocumentId = previousLastDocumentId;
    if (loadOptions && loadOptions?.isFreshFetch) {
      currentPreviousLastDocumentId = '';
    }
    const results = await dispatch(
      getUserCustomers({
        supplierId: currentSupplier?.id || '',
        previousLastDocumentId: currentPreviousLastDocumentId,
        pageSize,
        sortBy: 'name',
        sortOrder: 'asc',
      })
    );
    if (results.type === 'supplier/getUserCustomers/fulfilled') {
      if (results.payload) {
        const payload = results.payload as any;
        const cust: {
          buyer: Buyer;
          usersEmailsDetails: {
            email: string;
            userId: string;
            isInvitedUser: boolean;
            isStatementsEmailOptOut: boolean;
            isPaymentRemindersEmailOptOut: boolean;
          }[];
          documentsSummary: DocumentsSummary;
        }[] = payload.data as any;
        const lastDockId = payload.lastDocumentId;
        if (!customerCount && payload.customerCount) {
          setCustomerCount(payload.customerCount || 0);
        }
        if (lastDockId && lastDockId !== currentPreviousLastDocumentId) {
          setPreviousLastDocumentId(lastDockId);

          const count = Number(payload.customerCount);
          recursiveFetchCustomers(count, lastDockId, [...cust]);
        } else {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (mainRoles?.includes('SUPPLIER') && firebaseUserId && currentSupplier?.id) {
      setPreviousLastDocumentId('');
      setIsLoading(true);
      handleLoadMore({ isFreshFetch: true });
    }

    return () => {};
  }, [user?.id, firebaseUserId, currentSupplier?.id]);

  useEffect(() => {
    if (!customers || customers.length === 0) {
      setRows([]);
      return;
    }

    const isUsingAgingRanges =
      currentSupplier?.isUseAgingRanges &&
      currentSupplier?.agingRanges &&
      currentSupplier.agingRanges.length > 0;

    const branches = supplierBranches;
    const branchMap = new Map(branches.map((b) => [b.branchId, b.branchName || b.branchId]));

    // Resolves a buyer's assigned credit manager id to a display name, and the set of buyer ids
    // with a currently ACTIVE promise to pay - same joins Customers.tsx uses, needed here so a
    // saved Collection View's credit-manager/promise-to-pay criteria can be applied to this
    // report too (see the view-filtering in ArAgingTable.tsx).
    const creditManagerNameById = new Map(
      (supplierUsers ?? [])
        .filter((u) => u.roles.includes('SUB_SUPPLIER') || u.roles.includes('SUPPLIER'))
        .map((u) => [u.id, `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email])
    );
    const activePromiseBuyerIds = new Set(
      (supplierPromiseToPays ?? []).filter((p) => p.status === 'ACTIVE').map((p) => p.buyerId)
    );

    const flattenedRows: ArAgingRow[] = customers
      .filter((customer) => !customer.buyer?.isDeactivated)
      .map((customer) => {
        const buyer = customer.buyer!;
        const docsSummary = buyer.documentsSummary;

        let current = 0;
        let pastDue1To30 = 0;
        let pastDue31To60 = 0;
        let pastDue61To90 = 0;
        let pastDueOver90 = 0;

        if (isUsingAgingRanges && docsSummary?.agingBuckets) {
          // Map aging buckets to standard columns based on order
          docsSummary.agingBuckets.forEach((bucket) => {
            switch (bucket.order) {
              case 1:
                current = bucket.amount;
                break;
              case 2:
                pastDue1To30 = bucket.amount;
                break;
              case 3:
                pastDue31To60 = bucket.amount;
                break;
              case 4:
                pastDue61To90 = bucket.amount;
                break;
              case 5:
                pastDueOver90 = bucket.amount;
                break;
            }
          });
        } else {
          // Use fixed fields
          current = docsSummary?.totalCurrentDueInvoices ?? 0;
          pastDue1To30 = docsSummary?.totalPastDue1To30Invoices ?? 0;
          pastDue31To60 = docsSummary?.totalPastDue31To60Invoices ?? 0;
          pastDue61To90 = docsSummary?.totalPastDue61To90Invoices ?? 0;
          pastDueOver90 = docsSummary?.totalPastDueOver90Invoices ?? 0;
        }

        // Resolve branch name from supplier branches
        const branchId = buyer.branch || '';
        const branchName = branchId ? branchMap.get(branchId) || branchId : '';

        // Combine emails from quickbooks and user emails
        const allEmails = [
          ...(buyer.quickbooksCustomerEmails || []),
          ...(customer.usersEmailsDetails || []).map((u) => u.email),
        ];
        const uniqueEmails = Array.from(new Set(allEmails.filter(Boolean)));

        // Last login from buyer activities
        const lastLoggedIn = (buyer as any).activities?.lastLoggedIn;
        const lastLoginStr = lastLoggedIn
          ? new Date(lastLoggedIn).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '';

        // Credit utilized
        const creditLimit = buyer.creditAmount ?? 0;
        const totalDue = docsSummary?.totalAmountDueInvoices ?? 0;
        const creditUtilizedStr =
          creditLimit > 0 ? `${Math.round((totalDue / creditLimit) * 100)}%` : '';

        // Collection sequence/trigger enrollment - a buyer is attached to at most one of the
        // two at a time (the global mutual-exclusion rule enforced by claimBuyerEngagement on
        // the backend), so this resolves to a single status/name/step.
        const { collectionStatus, collectionName, collectionStep } =
          resolveBuyerCollectionEngagement({
            buyerId: buyer.id,
            buyerName: buyer.name || '',
            amount: totalDue,
            engagementsByBuyerId,
            progressByBuyerId,
            collectionSequences,
            collectionTriggers,
          });

        const totalPastDue = pastDue1To30 + pastDue31To60 + pastDue61To90 + pastDueOver90;

        const lastCollectionActivity = docsSummary?.lastCollectionActivity
          ? {
              date: toSafeISOString(docsSummary.lastCollectionActivity.date),
              type: docsSummary.lastCollectionActivity.activityType ?? null,
              summaryLine: docsSummary.lastCollectionActivity.summaryLine ?? null,
              countLast30Days: Number(docsSummary.lastCollectionActivity.countLast30Days ?? 0),
            }
          : null;

        const lastPaymentReceived = docsSummary?.lastPaymentReceived
          ? {
              date: toSafeISOString(docsSummary.lastPaymentReceived.date),
              amount: Number(docsSummary.lastPaymentReceived.amount ?? 0),
            }
          : null;

        return {
          id: buyer.id,
          quickbooksCustomerId: buyer.quickbooksCustomerId || '',
          businessName: buyer.name || '',
          branch: branchName,
          streetAddress: buyer.address || '',
          city: buyer.city || '',
          state: buyer.state || '',
          zipCode: buyer.zipCode || '',
          phoneNumber: buyer.phoneNumbers?.[0] || '',
          emails: uniqueEmails.join(', '),
          isCODCustomer: Boolean(buyer.isCODCustomer),
          terms: buyer.terms || '',
          lastLogin: lastLoginStr,
          creditUtilized: creditUtilizedStr,
          balance: docsSummary?.totalAmountDueInvoices ?? 0,
          current,
          pastDue1To30,
          pastDue31To60,
          pastDue61To90,
          pastDueOver90,
          totalPastDue,
          pastDuePct: 0,
          isBuyerOnCreditHold: Boolean((buyer as any).isBuyerOnCreditHold),
          lastCollectionActivity,
          lastPaymentReceived,
          creditManagerId: (buyer as any).asignedCreditManagerId ?? '',
          creditManagerName: creditManagerNameById.get((buyer as any).asignedCreditManagerId) ?? '',
          hasActivePromiseToPay: activePromiseBuyerIds.has(buyer.id),
          isFlagged: Boolean((buyer as any).isFlagged),
          collectionStatus,
          collectionName,
          collectionStep,
        };
      });

    const totalPastDueAcrossRows = flattenedRows.reduce((s, r) => s + r.totalPastDue, 0);
    const rowsWithPct = flattenedRows.map((r) => ({
      ...r,
      pastDuePct:
        totalPastDueAcrossRows > 0
          ? Math.round((r.totalPastDue / totalPastDueAcrossRows) * 10000) / 100
          : 0,
    }));

    setRows(rowsWithPct);
  }, [
    customers,
    currentSupplier,
    supplierBranches,
    supplierUsers,
    supplierPromiseToPays,
    collectionSequences,
    collectionTriggers,
    engagementsByBuyerId,
    progressByBuyerId,
  ]);

  return { rows, isLoading, totalCount: customerCount };
};
